/**
 * Time format constants for user preferences.
 * Supports 12-hour (AM/PM) and 24-hour time display formats.
 */

export const TIME_FORMAT_12H = '12h' as const;
export const TIME_FORMAT_24H = '24h' as const;

export type TimeFormat = typeof TIME_FORMAT_12H | typeof TIME_FORMAT_24H;

/**
 * Default time format used when no preference is set.
 */
export const DEFAULT_TIME_FORMAT: TimeFormat = TIME_FORMAT_12H;

/**
 * Locales that typically use 12-hour time format.
 * Based on common conventions in these regions.
 * Future proofed in case we add more languages
 */
const LOCALES_USING_12H = [
    'en-US', // United States
    'en-CA', // Canada (English)
    'en-PH', // Philippines
    'en-AU', // Australia
    'en-NZ', // New Zealand
    'en-IN', // India
];

const normalizeLocaleForComparison = (locale: string): string => {
    const [languageCode, regionCode] = locale.replace(/_/g, '-').split('-');
    if (!languageCode) return '';
    if (!regionCode) return languageCode.toLowerCase();

    return `${languageCode.toLowerCase()}-${regionCode.toUpperCase()}`;
};

/**
 * Get the default time format for a given locale.
 * Returns 12h for locales that commonly use AM/PM, 24h otherwise.
 */
export const getDefaultTimeFormatForLocale = (locale: string | null | undefined): TimeFormat => {
    if (!locale) return DEFAULT_TIME_FORMAT;

    const normalizedLocale = normalizeLocaleForComparison(locale);

    return LOCALES_USING_12H.includes(normalizedLocale) ? TIME_FORMAT_12H : TIME_FORMAT_24H;
};
